import { DateRange } from 'react-day-picker'
import { UpdatedPrices } from '../utils/checkout-prices'
import { ProductSource, ProductType } from '../types/product'
import type { Building } from '../types/building'

export type PaymentMethod = 'credit card' | 'debit card'

export interface ProductData {
  productName: string
  productBuilding: string
  productType: ProductType
}
export interface TrackEventParams {
  category?: string
  action?: string
  label?: string
  value?: number
  paymentMethod?: PaymentMethod
}

export interface TrackPageParams {
  page_variant?: string
  content_type?: string
  content_id?: string
  page_name?: string
  page_url_clean?: string
  business_region?: string
  site_name?: string
  navigation_variant?: string
  footer_variant?: string
}

interface FormEventVars {
  form_name: string
  form_type: string
  form_id: string
  event_action?: 'subscribed for updates' | 'did not subscribe for updates'
}
export interface TrackFormParams {
  event: 'form_submit_ts'
  event_origin: string
  event_vars: FormEventVars
}
export interface TrackEmailSubscribeFormParams {
  event: 'email_subscribe'
  event_origin: 'front-end'
  event_vars: FormEventVars
}
interface TourEventVars {
  form_name: string
  form_type: 'tour request'
  form_id: string
  event_action: 'subscribed for updates' | 'did not subscribe for updates'
  property_title: string
  location_id: string
  category_descriptor: string
}

export interface TrackTourFormParams {
  event: 'contact_tour_request_form_submitted'
  event_origin: string
  event_vars: TourEventVars
}

export interface TrackCtaClickParams {
  text: string
  style: string
  href: string
  component: string
  pageUrl: string
}

export interface TrackFormStartParams {
  event: 'form_start_ts'
  event_origin: 'front-end'
  event_vars: FormEventVars
}

type EventTypes =
  | 'pageView'
  | 'customEvent'
  | 'contact_tour_request_form_submitted'
  | 'form_submit_ts'
  | 'view_item'
  | 'add_to_cart'
  | 'purchase'
  | 'add_payment_info'
  | 'cta_clicked'
  | 'begin_checkout'
  | 'add_shipping_info'
  | 'contact_general_inquiry_form_submitted'
  | 'purchase_custom_ts'
  | 'contact_pricing_inquiry_form_submitted'
  | 'tab_clicked'
  | 'toggle_clicked'
  | 'form_start_ts'
  | 'email_subscribe'
  | 'map_clicked'
  | 'property_result_clicked'
  | 'content_card_clicked'
  | 'navigation_menu_clicked'
  | 'pageview_custom'

export interface TrackViewItemParams {
  platformId: string | number
  currencyCode: string
  price: number
  discount: number
  coupon?: string
  name: string
  location: string
  type: ProductType
  bookingType: 'instant book' | 'inquiry only'
  capacity: number
  hourlyRate: number
  source: ProductSource
}

export interface TrackAddToCartParams extends TrackViewItemParams {
  dateRange: DateRange
}
export interface EcommerceItem {
  item_id: string
  item_name: string
  coupon: string
  discount: number
  price: number
  quantity: number
  item_brand: string
  item_category: string
  item_category2: string
  item_category3: string
  item_category4: string
  item_category5: string
  location_id: string
  product_name: string
  product_id: string
  product_type: string
  product_capacity: string
  product_hourly_rate: number
  product_source: string
  booking_start_time: string
  booking_end_time: string
  booking_duration: number
  booking_date_selected: string
}
interface EcommerceData {
  transaction_id: string
  value: number
  tax: number
  currency: string
  coupon: string
  items: EcommerceItem[]
}
export interface TrackPurchaseEventParams {
  event: 'purchase'
  ecommerce: EcommerceData
}

interface EcommerceCheckoutFlowData {
  currency: string
  value: number
  coupon: string
  items: EcommerceItem[]
}

export interface TrackEnterCheckoutFlowParams {
  event: 'begin_checkout'
  ecommerce: EcommerceCheckoutFlowData
}

export interface TrackPaymentInfo {
  event: 'add_payment_info'
  ecommerce: {
    currency: string
    value: number
    coupon: string
    payment_type: string
    items: EcommerceItem[]
  }
}

export interface TrackAddShippingInfo {
  event: 'add_shipping_info'
  ecommerce: {
    currency: string
    value: number
    coupon: string
    items: EcommerceItem[]
  }
}

interface PurchaseCustomEventVars {
  value: number
  product_name: string
  product_type: string
  product_capacity: string
  product_quantity_custom_ts: number
  product_hourly_rate: number
  product_source: string
  product_id: string
  location_id: string
  transaction_id: string
}

export interface TrackPurchaseCustomParams {
  event: 'purchase_custom_ts'
  event_origin: string
  event_vars: PurchaseCustomEventVars
}

export interface TrackGeneralInquiryFormSubmitParams {
  event: 'contact_general_inquiry_form_submitted'
  event_origin: 'front-end'
  event_vars: GeneralInquiryFormSubmitVars
}
interface GeneralInquiryFormSubmitVars {
  form_name: string
  form_type: string
  form_id: string
  event_action: 'subscribed for updates' | 'did not subscribe for updates'
  property_title: string
  location_id: string
}

export interface TrackPricingInquirySubmitParams {
  event: 'contact_pricing_inquiry_form_submitted'
  event_origin: 'front-end'
  event_vars: PricingInquirySubmitVars
}
interface PricingInquirySubmitVars {
  product_name: string
  product_type: string
  product_id: string
  product_capacity: string | 'none available'
  product_source: string
  location_id: string
}

export interface TrackTabClickParams {
  event: 'tab_clicked'
  event_origin: 'front-end'
  event_vars: TabClickVars
}
interface TabClickVars {
  title_descriptor: string
  component_name: string
  page_url_clean: string
}

export interface TrackToggleClickParams {
  event: 'toggle_clicked'
  event_origin: 'front-end'
  event_vars: ToggleClickVars
}
interface ToggleClickVars {
  title_descriptor: string
  event_action: 'toggle turned on' | 'toggle turned off'
  component_name: string
  page_url_clean: string
}

export interface TrackMapCardClickParams {
  event: 'map_clicked'
  event_origin: 'front-end'
  event_vars: MapCardClickVars
}
interface MapCardClickVars {
  title_descriptor: string
  event_action: 'map card opened' | 'map card cta clicked'
  link_text: string
  link_url: 'not applicable - map card opened' | string
  type_descriptor: string
  component_name: string
  page_url_clean: string
}

export interface TrackPropertyResultClickParams {
  event: 'property_result_clicked'
  event_origin: 'front-end'
  event_vars: PropertyResultClickVars
}
interface PropertyResultClickVars {
  property_title: string
  property_type: string
  business_region: string
  event_action: 'full card clicked' | `cta clicked: ${string}`
  link_url: string
  component_name: string
  page_url_clean: string
}

export interface TrackContentCardClickParams {
  event: 'content_card_clicked'
  event_origin: 'front-end'
  event_vars: ContentCardClickVars
}
interface ContentCardClickVars {
  content_headline: string
  content_section: string
  position_number: 'not applicable - content block'
  link_url: string
  component_name: string
  page_url_clean: string
}

export interface TrackNavigationMenuClickParams {
  event: 'navigation_menu_clicked'
  event_origin: 'front-end'
  event_vars: NavigationMenuClickVars
}

interface NavigationMenuClickVars {
  navigation_menu_item_title: string
  navigation_menu_heading: string
  navigation_menu_type: string
  link_url: string
  type_descriptor: string
  component_name: string
  page_url_clean: string
}

export interface TrackNavigationParams {
  event: 'pageview_custom'
  event_origin: 'front-end'
  event_vars: NavigationVars
}
interface NavigationVars {
  page_variant: string
  page_url_custom: string
  page_url_clean: string
  page_path_custom: string
  page_title_custom: string
  page_referrer_custom: string
  navigation_variant?: string
  footer_variant?: string
}

export interface AnalyticsDataLayer {
  event?: EventTypes
  [key: string]: any
}

export interface AnalyticsProvider {
  initialize(): void
  trackEvent(params: TrackEventParams): void
  trackPageView(url: string): void
  trackPage(data: TrackPageParams): void
  pushDataLayer(data: AnalyticsDataLayer): void
  pushDataLayerSync(data: AnalyticsDataLayer): void
  trackPaymentMethod(data: PaymentMethod): void
  trackForm(data: TrackFormParams): void
  trackTourForm(data: TrackTourFormParams): void
  trackViewItem(data: TrackViewItemParams): void
  trackAddToCart(data: TrackAddToCartParams): void
  trackPurchaseEvent(data: TrackPurchaseEventParams): void
  trackPaymentInfo(data: TrackPaymentInfo): void
  trackEnterCheckoutFlow(data: TrackEnterCheckoutFlowParams): void
  trackAddShippingInfo(data: TrackAddShippingInfo): void
  trackCtaClick(data: TrackCtaClickParams): void
  trackGeneralInquiryForm(data: TrackGeneralInquiryFormSubmitParams): void
  trackPurchaseCustom(data: TrackPurchaseCustomParams): void
  trackPricingInquiryForm(data: TrackPricingInquirySubmitParams): void
  trackTabClick(data: TrackTabClickParams): void
  trackToggleClick(data: TrackToggleClickParams): void
  trackFormStart(data: TrackFormStartParams): void
  trackEmailSubscribeForm(data: TrackEmailSubscribeFormParams): void
  trackMapCardClick(data: TrackMapCardClickParams): void
  trackPropertyResultClick(data: TrackPropertyResultClickParams): void
  trackContentCardClick(data: TrackContentCardClickParams): void
  trackNavigationMenuClick(data: TrackNavigationMenuClickParams): void
  trackNavigationParams(data: TrackNavigationParams): void
  generateItemName(data: ProductData): string
  getTotalDiscount(data: UpdatedPrices): number
  setBuildings(buildings: Building[]): void
  getProductRegion(building: string): string
  getProductLocationId(building: string, source: ProductSource): string
  getBookingStartTime(dateRange: DateRange, building: string): string
  getBookingEndTime(dateRange: DateRange, building: string): string
  getProductLocationName(building: string): string
  getProductTypeName(spaceType: ProductType): string
  getBookingDurationTime(dateRange: DateRange, building: string): number
  getBookingSelectedDates(dateRange: DateRange, building: string): string
}

export class AnalyticsService {
  private static instance: AnalyticsService
  private provider: AnalyticsProvider

  constructor(provider: AnalyticsProvider) {
    this.provider = provider
    this.provider.initialize()
  }

  public static getInstance(provider: AnalyticsProvider): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService(provider)
    }
    return AnalyticsService.instance
  }

  trackEvent(params: TrackEventParams) {
    this.provider.trackEvent(params)
  }

  trackPageView(url: string) {
    this.provider.trackPageView(url)
  }

  trackPage(data: TrackPageParams) {
    this.provider.trackPage(data)
  }

  trackViewItem(data: TrackViewItemParams) {
    this.provider.trackViewItem(data)
  }

  trackAddToCart(data: TrackAddToCartParams) {
    this.provider.trackAddToCart(data)
  }

  trackPaymentMethod(paymentMethod: PaymentMethod) {
    this.provider.trackPaymentMethod(paymentMethod)
  }

  trackPurchaseEvent(data: TrackPurchaseEventParams) {
    this.provider.trackPurchaseEvent(data)
  }

  trackPurchaseCustom(data: TrackPurchaseCustomParams) {
    this.provider.trackPurchaseCustom(data)
  }

  trackPaymentInfo(data: TrackPaymentInfo) {
    this.provider.trackPaymentInfo(data)
  }

  trackEnterCheckoutFlow(data: TrackEnterCheckoutFlowParams) {
    this.provider.trackEnterCheckoutFlow(data)
  }

  trackAddShippingInfo(data: TrackAddShippingInfo) {
    this.provider.trackAddShippingInfo(data)
  }

  trackCtaClick(data: TrackCtaClickParams) {
    this.provider.trackCtaClick(data)
  }

  trackGeneralInquiryForm(data: TrackGeneralInquiryFormSubmitParams) {
    this.provider.trackGeneralInquiryForm(data)
  }

  trackPricingInquiryForm(data: TrackPricingInquirySubmitParams) {
    this.provider.trackPricingInquiryForm(data)
  }

  trackTabClick(data: TrackTabClickParams) {
    this.provider.trackTabClick(data)
  }

  trackToggleClick(data: TrackToggleClickParams) {
    this.provider.trackToggleClick(data)
  }

  trackEmailSubscribeForm(data: TrackEmailSubscribeFormParams) {
    this.provider.trackEmailSubscribeForm(data)
  }

  trackFormStart(data: TrackFormStartParams) {
    this.provider.trackFormStart(data)
  }

  trackMapCardClick(data: TrackMapCardClickParams) {
    this.provider.trackMapCardClick(data)
  }

  trackPropertyResultClick(data: TrackPropertyResultClickParams) {
    this.provider.trackPropertyResultClick(data)
  }

  trackContentCardClick(data: TrackContentCardClickParams) {
    this.provider.trackContentCardClick(data)
  }

  trackNavigationMenuClick(data: TrackNavigationMenuClickParams) {
    this.provider.trackNavigationMenuClick(data)
  }

  trackNavigation(data: TrackNavigationParams) {
    this.provider.trackNavigationParams(data)
  }

  generateItemName(data: ProductData) {
    return this.provider.generateItemName(data)
  }

  getTotalDiscount(data: UpdatedPrices) {
    return this.provider.getTotalDiscount(data)
  }

  setBuildings(buildings: Building[]) {
    this.provider.setBuildings(buildings)
  }

  getProductRegion(building: string) {
    return this.provider.getProductRegion(building)
  }

  getProductLocationId(building: string, source: ProductSource) {
    return this.provider.getProductLocationId(building, source)
  }

  getBookingStartTime(dateRange: DateRange, building: string) {
    return this.provider.getBookingStartTime(dateRange, building)
  }

  getBookingEndTime(dateRange: DateRange, building: string) {
    return this.provider.getBookingEndTime(dateRange, building)
  }

  getProductLocationName(building: string): string {
    return this.provider.getProductLocationName(building)
  }

  getProductTypeName(spaceType: ProductType): string {
    return this.provider.getProductTypeName(spaceType)
  }

  getBookingDurationTime(dateRange: DateRange, building: string) {
    return this.provider.getBookingDurationTime(dateRange, building)
  }

  getBookingSelectedDates(dateRange: DateRange, building: string) {
    return this.provider.getBookingSelectedDates(dateRange, building)
  }

  trackTourForm(data: TrackTourFormParams) {
    this.provider.trackTourForm(data)
  }

  trackForm(data: TrackFormParams) {
    this.provider.trackForm(data)
  }
}
